const onboarding = {
  common: {
    saveExit: 'Save and exit',
    back: 'Back',
    optional: 'Optional',
    collapse: 'Collapse',
    addPlatform: '+ Add platform',
    join: 'Add',
    remove: 'Remove {name}',
    publicPlatform: 'Public platform',
    publicLink: '{name} public link',
    pastePublicLink: 'Paste a public profile link',
  },
  status: {
    autoSave: 'Your progress is saved automatically',
    saving: 'Saving…',
    saved: 'Saved just now',
    safeToLeave: 'Progress saved. You can leave safely.',
  },
  exit: {
    title: 'Save this progress?',
    body: 'Your form fields, links and material list are saved in this browser so you can continue later.',
    fileTitle: 'Local files must be selected again',
    fileWarning: 'For browser security, Navigator cannot retain local file contents after you leave. When you return, select those files again before activation; nothing is uploaded by saving this draft.',
    continue: 'Continue editing',
    confirm: 'Save and exit',
  },
  rail: {
    aria: 'Enterprise onboarding steps',
    kicker: 'Enterprise onboarding',
    title: 'Let Navigator understand your business first',
    copy: 'Confirm only the essential facts. Navigator handles technical configuration, model choices and background tasks.',
    sovereignty: 'Only project materials you choose to upload are used by Navigator.',
    appearance: 'Appearance · {theme}',
  },
  theme: { system: 'System', light: 'Light', dark: 'Dark' },
  steps: {
    space: { label: 'Create space', detail: 'Establish your business identity' },
    project: { label: 'Create project', detail: 'Confirm brand and markets' },
    materials: { label: 'Add materials', detail: 'Choose production materials' },
    review: { label: 'Review', detail: 'Prepare for diagnosis' },
  },
  roles: {
    founder: 'Founder / Administrator',
    marketing: 'Head of marketing',
    brand: 'Head of brand',
    export: 'Head of international sales',
    collaborator: 'Project collaborator',
  },
  validation: {
    identity: 'Enter your name and a valid work email first',
    project: 'Complete the brand, project, product and buyer information',
    market: 'Select at least one target market',
    language: 'Choose a default report language and at least one target content language',
    materials: 'Select retained local files again or remove those you no longer need',
    reselectFile: 'Select this local file again before continuing',
    productionConsent: 'Confirm the production-use authorization before continuing',
    profile: 'Wait for the enterprise profile and review all retained items',
    channel: 'Paste a public platform link first',
    website: 'Enter the company website first',
  },
  toast: {
    filesAdded: '{count} files added for Navigator. Remove any selected by mistake.',
    filesRejectedBySize: '{count} files were not added. Each file, and all directly read PDF, image, audio and video files combined, must stay within 64 MB.',
    filesAddedWithSizeRejected: '{added} files added; {rejected} were not added because they exceeded the 64 MB file or direct-media total limit.',
    fileRemoved: '{name} removed. It will not be uploaded.',
    demoWebsite: 'Public pages have produced a candidate draft',
    websiteRecorded: 'Website recorded. Public collection begins after activation.',
    completed: 'Enterprise onboarding confirmed. Preparing diagnosis.',
    completionFailed: 'Onboarding could not be completed. Your draft has been retained.',
  },
  step1: {
    eyebrow: 'Get started with Navigator',
    title: 'Create your enterprise space',
    intro: 'This space belongs only to your enterprise. Every future project, diagnosis, file and action remains isolated here.',
    name: 'Your name',
    role: 'Your role in the enterprise',
    email: 'Work email',
    emailNote: 'Used to sign in and receive report-ready notifications',
    trialTitle: 'Simplified trial activation',
    trialBody: 'An invited identity creates the enterprise space directly. No password, API key or model account is required.',
    next: 'Create space and continue',
  },
  step2: {
    eyebrow: 'Enterprise project',
    title: 'Tell us where to begin',
    intro: 'Your website, public profiles and product materials can all be starting points. Navigator does the first pass; you confirm and correct it.',
    website: 'Company website',
    websiteNote: 'You can continue without one',
    reading: 'Reading…',
    recorded: 'Recorded',
    readWebsite: 'Read website',
    noWebsite: 'No company website yet',
    discoveryNoWebsiteTitle: 'A project can start without a website',
    discoveryNoWebsiteBody: 'We will draft the enterprise profile from the product description, public links and authorized project materials.',
    discoveryDemoTitle: 'Public pages produced a draft',
    discoveryDemoBody: 'You still need to confirm the findings; they cannot become enterprise truth automatically.',
    discoveryRecordedTitle: 'The website will be read after activation',
    discoveryRecordedBody: 'Only the URL is saved now. The authenticated collection service will read it later; the browser does not fabricate results.',
    discoveryCandidateTitle: 'Public information is only a candidate draft',
    discoveryCandidateBody: 'Websites, social profiles, stores and industry pages help us understand the enterprise, but become enterprise truth only after your confirmation.',
    brand: 'Brand name',
    project: 'Project name',
    product: 'Main product or product line',
    productDescription: 'Describe it in one sentence',
    targetBuyer: 'Typical customer or buyer',
    next: 'Confirm project and upload files',
  },
  market: {
    title: 'Target markets',
    note: 'Regions are shortcuts only; countries or territories are saved',
    closePicker: 'Close market selector',
    openPicker: 'Select countries or territories · {count}',
    empty: 'None selected. At least one exact country or territory is required.',
    more: '{count} more · Expand',
    collapseSelected: 'Collapse selected markets',
    presetTitle: 'Common commercial market groups',
    presetAria: 'Common market groups',
    presets: {
      china: 'Mainland China',
      northAmerica: 'North America · US / CA / MX',
      asean: 'ASEAN',
      gcc: 'GCC',
      eea: 'EU / European Economic Area',
      global: 'Global markets',
    },
    search: 'Search country or territory, ISO / M49 code',
    filterAria: 'Filter by United Nations geographic hierarchy',
    all: 'All countries and territories · {count}',
    regionAll: '{region} · All',
    listStatus: '{visible} countries or territories · {selected} selected',
    deselectScope: 'Deselect current scope',
    selectScope: 'Select current scope',
    listAria: 'Country and territory list',
    registryTitle: 'Saved as:',
    registryBody: 'ISO 3166-1 alpha-2 + UN M49 numeric code. Global, regional, subregional and commercial groups only expand selections and are never saved as vague market values. The registry is versioned independently.',
    summaryMore: '{count} markets total',
  },
  geo: {
    asia: 'Asia', europe: 'Europe', americas: 'Americas', africa: 'Africa', oceania: 'Oceania', other: 'Other territories',
    easternAsia: 'Eastern Asia', southEasternAsia: 'South-eastern Asia', southernAsia: 'Southern Asia', centralAsia: 'Central Asia', westernAsia: 'Western Asia',
    easternEurope: 'Eastern Europe', northernEurope: 'Northern Europe', southernEurope: 'Southern Europe', westernEurope: 'Western Europe',
    northernAmerica: 'Northern America', centralAmerica: 'Central America', southAmerica: 'South America', caribbean: 'Caribbean',
    northernAfrica: 'Northern Africa', easternAfrica: 'Eastern Africa', middleAfrica: 'Middle Africa', southernAfrica: 'Southern Africa', westernAfrica: 'Western Africa',
    australiaNewZealand: 'Australia and New Zealand', melanesia: 'Melanesia', micronesia: 'Micronesia', polynesia: 'Polynesia',
  },
  language: {
    defaultTitle: 'Default work and report language',
    defaultNote: 'One project default',
    defaultHint: 'Navigator uses this language for diagnosis reports, strategy recommendations and action plans. It remains independent from the interface language.',
    contentTitle: 'Target content languages',
    contentNote: 'Select one or more',
    contentHint: 'Used for market-facing content and multilingual production. A single task can override these project defaults later.',
  },
  channel: {
    title: 'Public profiles and content channels',
    body: 'These links can later serve as both content sources and publishing assets.',
    empty: 'No public platforms added. You can add them later in enterprise settings.',
    selectAria: 'Select platform',
    china: 'Major platforms in China',
    overseas: 'Major international platforms',
    paste: 'Paste a public profile or store link',
  },
  platforms: {
    wechat: 'WeChat Official Account', channels: 'WeChat Channels', douyin: 'Douyin', xiaohongshu: 'Xiaohongshu', weibo: 'Weibo', bilibili: 'Bilibili', zhihu: 'Zhihu', tmall: 'Tmall', jd: 'JD.com', p1688: '1688', madeinchina: 'Made-in-China.com',
    x: 'X', substack: 'Substack', youtube: 'YouTube', instagram: 'Instagram', linkedin: 'LinkedIn', tiktok: 'TikTok', amazon: 'Amazon', other: 'Other public platform',
  },
  step3: {
    eyebrow: 'Project production materials',
    title: 'Add materials for Navigator',
    intro: 'Files remain on this device until enterprise-profile review. Files selected here are added to this enterprise project for diagnosis and content production.',
    uploadTitle: 'Drop files here or click to choose',
    uploadBody: 'Uploads begin at enterprise-profile review · 64 MB per file · 64 MB total for directly read PDF, image, audio and video files',
    legendAria: 'Material-use guide',
    emptyTitle: 'No project materials added yet',
    emptyBody: 'If public links are sufficient, continue to review. Materials can be added later.',
    gateOpenTitle: 'Project materials are ready.',
    gateOpenBody: 'Every retained file will be used in this Navigator project. Continue to the final review.',
    gateClosedTitle: 'Local files must be selected again.',
    gateClosedBody: '{count} retained files are only draft reminders. Select those files again or remove them before continuing.',
    analysisConsent: 'By continuing, you confirm that you may provide these materials and permit Navigator to upload and read them for the initial enterprise profile. Necessary excerpts may later be sent to the designated AI provider through the approved task path.',
    next: 'Review materials',
  },
  domain: {
    publicShort: 'Project material', privateShort: 'Reserved for V2', excludeShort: 'Remove', unresolved: 'Select file again',
    publicTitle: 'Used in this project', publicBody: 'Files you select are added to this enterprise project for diagnosis and content production. This does not publish them publicly.',
    privateTitle: 'Private Vault is not available in V1', privateBody: 'Private/local Vault handling is reserved for the Monolithos host or a later Navigator version.',
    excludeTitle: 'Remove this file', excludeBody: 'Removed files are not part of the project and are not uploaded to Navigator.',
    groupAria: 'Actions for {name}',
  },
  profile: {
    eyebrow: 'Review before activation', title: 'This is how Navigator currently understands your company.', intro: 'Review all nine items. Keep what is right, edit what is not, and exclude suggestions you do not want to use.', introNoWebsite: 'Review all nine items. Having no website does not block setup or count as poor source quality.',
    generatingEyebrow: 'Building your business profile', generatingTitle: 'Navigator is understanding your business', generatingIntro: 'Navigator is reading the information and materials you confirmed, then organizing them into nine reviewable profile items. They will appear automatically when ready.', revealingEyebrow: 'Profile formed', revealingTitle: 'Presenting each result', revealingIntro: 'All nine candidate results are complete. Navigator is placing them into the review interface one by one so you can see each item arrive.',
    truthTitle: 'Candidate information only. ', truthBody: 'Nothing below becomes enterprise truth or enters diagnosis until you confirm it.',
    loading: 'Preparing the initial enterprise profile…', progressInputs: 'Receive confirmed project information', progressSources: 'Read authorized public sources and materials', progressAnalyze: 'Understand the business, products and markets', progressFinish: 'Organize nine business-profile items', progressReceiving: 'Receiving confirmed information', progressUploading: 'Uploading project materials · {completed}/{total}', progressReading: 'Extracting readable content and public information', progressAnalyzing: 'Understanding your business and products', progressFinalizing: 'Organizing nine candidate results', progressElapsed: '{seconds}s elapsed', progressProjectForm: 'Project form received', progressPublicLinks: 'Public links · {count}', progressMaterials: 'Project materials · {count}', progressDimensions: 'Nine decision areas being formed', progressWait: 'You can stay on this page. Results appear automatically when ready; please do not submit again.', revealPanelEyebrow: 'Delivering results', revealPanelTitle: 'Nine business-profile items are taking their places', revealCompleteTitle: 'All nine business-profile items are ready', revealPreparing: 'Checking the result structure…', revealCount: '{count} / {total}', revealDisclosure: 'These are completed candidate results being presented one by one.', revealSkip: 'View the complete result', errorTitle: 'The profile could not be prepared.', errorBody: 'No partial or fabricated result was used. Return to the source step and try again.', backToSources: 'Back to sources',
    sourcesAria: 'Sources used for the initial profile', sourcesTitle: 'Sources used', userInput: 'Project form · {count}', publicLinks: 'Public links · {count}', materialList: 'Material records · {count}', publicSearch: 'Light public search · {count}', publicSearchUnknown: 'Light public search completed · count unavailable', publicSearchNotRun: 'Public search not run', noWebsite: 'No website yet', fileContentsPending: 'No machine-readable file text was available for this review.', fileContentsRead: 'Readable text extracted from {count} materials.', fileContentsLimited: '{count} materials were only partly readable or unsupported; no missing text was treated as fact.',
    found: 'Found', review: 'Review', notApplicable: 'Not applicable', sectionTitle: 'Initial enterprise profile', sectionHint: 'Open any item to edit', statusFound: 'Found', statusReview: 'Review', statusInsufficient: 'Needs input', statusNotApplicable: 'Not applicable',
    fieldBrandIdentity: 'Brand name and aliases', fieldDomainFootprint: 'Website and related domains', fieldProductLines: 'Core product lines', fieldMarketsLanguages: 'Market footprint and language capability', fieldBuyerTypes: 'Likely primary buyers', fieldCompetitors: 'Initial competitor references', fieldPublicContent: 'Public channels and content assets', fieldWebsiteShape: 'Current website setup', fieldCompanyType: 'Initial company type',
    sourceUserInput: 'Your project form', sourceSystem: 'Navigator boundary check', edit: 'Edit', collapseEdit: 'Close editor', exclude: 'Do not use this suggestion', restore: 'Use this suggestion again', editLabel: 'Corrected content', cancel: 'Cancel', save: 'Save change',
  },
  step4: {
    eyebrow: 'Onboarding complete',
    title: 'Your enterprise project is ready',
    intro: 'Review once more. Selected materials are uploaded to this enterprise project after confirmation and may be used for diagnosis and content production.',
    vaultState: 'Enterprise project ready to be created · Source materials confirmed',
    project: 'Project', website: 'Website', product: 'Product', market: 'Market', noWebsite: 'No company website',
    defaultOutputLanguage: 'Default report language', contentLanguages: 'Target content languages',
    domains: 'Project production materials',
    authorizedCount: 'project materials',
    nextTitle: 'Next: confirm diagnosis scope',
    nextBody: 'Navigator will draft the enterprise profile, initial competitors and question pool from the website, public links and selected project materials. Diagnosis starts only after your confirmation.',
    consent: 'I confirm that I am authorized to provide the retained materials and permit Navigator to store and use them for this project’s diagnosis and content production.',
    consentNote: 'Source materials, diagnosis and operating data, and generated marketing outputs remain isolated by tenant and project in the current Navigator project space. Do not upload passwords, API keys, private keys, or data you are not authorized to provide.',
    finishing: 'Confirming…',
    finish: 'Finish activation and prepare diagnosis',
  },
} as const

export default onboarding
